import { useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import FormField, { fieldInputStyle } from "../components/ui/FormField";
import Loading, { ErrorBox } from "../components/ui/Loading";
import {
  updateMe,
  uploadLogo,
  deleteLogo,
  TIPO_CHAVE_PIX,
  TIPO_CHAVE_PIX_LABEL,
} from "../services/profileService";

const EMPTY = {
  nome: "",
  empresa: "",
  documento: "",
  email: "",
  telefone: "",
  bancoNome: "",
  bancoAgencia: "",
  bancoConta: "",
  // chavesPix: lista dinâmica [{tipo: "0", valor: "..."}]. Substitui os
  // 2 campos escalares antigos (chavePix + tipoChavePix); backend aceita
  // ambos por 1 versão pra tolerar cliente antigo cacheado.
  chavesPix: [],
};

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
// Limite espelha o backend (ProfileController.MaxChavesPix) — teto real do PIX BR.
const MAX_CHAVES_PIX = 5;

export default function PerfilPage() {
  const { C } = useTheme();
  // profile e refreshProfile vêm do AuthContext — a sidebar consome o mesmo
  // objeto, então chamar refreshProfile() depois de salvar/upload/delete
  // atualiza página e sidebar juntas.
  const { profile, refreshProfile } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Logo
  const fileInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState(null);
  // Preview otimista: dataURL gerado pelo FileReader enquanto o upload sobe.
  // Se o upload falhar, limpa — e a exibição volta pra profile.logoUrl antigo.
  const [optimisticPreview, setOptimisticPreview] = useState(null);

  // Sincroniza o form a partir do profile do context — uma vez só.
  // AuthContext já dispara refreshProfile() sozinho quando o user entra.
  useEffect(() => {
    if (profile && !loaded) {
      // chavesPix vem do backend como array [{tipo, valor}]. Se o backend
      // ainda não populou (banco recém-migrado, edge case), fallback pro
      // escalar profile.chavePix — assim a UI mostra a chave existente
      // mesmo antes do 1º save que sincroniza.
      let chavesIniciais = Array.isArray(profile.chavesPix)
        ? profile.chavesPix.map((c) => ({
            tipo: c.tipo != null ? String(c.tipo) : "0",
            valor: c.valor ?? "",
          }))
        : [];
      if (chavesIniciais.length === 0 && profile.chavePix) {
        chavesIniciais = [{
          tipo: profile.tipoChavePix != null ? String(profile.tipoChavePix) : "0",
          valor: profile.chavePix,
        }];
      }

      setForm({
        nome: profile.nome ?? "",
        empresa: profile.empresa ?? "",
        documento: profile.documento ?? "",
        email: profile.email ?? "",
        telefone: profile.telefone ?? "",
        bancoNome: profile.bancoNome ?? "",
        bancoAgencia: profile.bancoAgencia ?? "",
        bancoConta: profile.bancoConta ?? "",
        chavesPix: chavesIniciais,
      });
      setLoaded(true);
    }
  }, [profile, loaded]);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function updateChave(idx, campo, value) {
    setForm((f) => ({
      ...f,
      chavesPix: f.chavesPix.map((c, i) => (i === idx ? { ...c, [campo]: value } : c)),
    }));
  }

  function addChave() {
    setForm((f) =>
      f.chavesPix.length >= MAX_CHAVES_PIX
        ? f
        : { ...f, chavesPix: [...f.chavesPix, { tipo: "0", valor: "" }] });
  }

  function removeChave(idx) {
    setForm((f) => ({ ...f, chavesPix: f.chavesPix.filter((_, i) => i !== idx) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nome.trim()) {
      setError("Nome é obrigatório.");
      return;
    }
    if (!form.documento.trim()) {
      setError("Documento/IVA é obrigatório.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const chavesPayload = form.chavesPix
        .map((c) => ({ tipo: Number(c.tipo), valor: (c.valor || "").trim() }))
        .filter((c) => c.valor);

      await updateMe({
        nome: form.nome.trim(),
        empresa: form.empresa.trim() || null,
        documento: form.documento.trim(),
        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        bancoNome: form.bancoNome.trim() || null,
        bancoAgencia: form.bancoAgencia.trim() || null,
        bancoConta: form.bancoConta.trim() || null,
        // Escalares mandados como null — backend usa o array como source of
        // truth e sincroniza os vestígios sozinho.
        chavePix: null,
        tipoChavePix: null,
        chavesPix: chavesPayload,
      });
      await refreshProfile();
      setToast("Perfil atualizado.");
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao salvar perfil.");
    } finally {
      setSaving(false);
    }
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0];
    // Limpa o value do input pra permitir re-selecionar o mesmo arquivo depois.
    e.target.value = "";
    if (!file) return;

    setLogoError(null);

    // Validação client-side (espelho do backend — frontend dá feedback mais rápido).
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      setLogoError("Formato inválido. Use PNG ou JPG.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("Arquivo excede o limite de 2 MB.");
      return;
    }

    // Preview otimista — enquanto o upload sobe, já mostra a imagem local.
    const reader = new FileReader();
    reader.onload = () => setOptimisticPreview(reader.result);
    reader.readAsDataURL(file);

    setUploadingLogo(true);
    try {
      await uploadLogo(file);
      await refreshProfile();
      setOptimisticPreview(null); // profile.logoUrl agora tem a nova URL versionada
    } catch (err) {
      setOptimisticPreview(null); // reverte pra imagem anterior do profile
      setLogoError(err?.response?.data?.message || "Erro ao enviar logo.");
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleLogoRemove() {
    setLogoError(null);
    setUploadingLogo(true);
    try {
      await deleteLogo();
      await refreshProfile();
    } catch (err) {
      setLogoError(err?.response?.data?.message || "Erro ao remover logo.");
    } finally {
      setUploadingLogo(false);
    }
  }

  const inputStyle = fieldInputStyle(C);

  if (!loaded) return <Loading />;

  // Fonte da exibição da logo: preview otimista > profile.logoUrl > placeholder.
  const logoSrc = optimisticPreview || profile?.logoUrl || null;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
          Meu Perfil
        </h1>
        <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
          Seus dados pessoais e bancários — aparecem no PDF dos orçamentos.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          background: C.bgCard,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
          padding: 24,
          maxWidth: 820,
        }}
      >
        {/* ─── Dados pessoais ─── */}
        <div style={sectionLabel(C)}>Dados pessoais</div>

        {/* Logo da empresa — primeiro elemento da seção */}
        <div
          style={{
            display: "flex",
            gap: 18,
            alignItems: "flex-start",
            marginBottom: 18,
          }}
        >
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              background: C.bgCardHover,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
              opacity: uploadingLogo ? 0.6 : 1,
              transition: "opacity 0.15s",
            }}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="Logo da empresa"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            ) : (
              <div style={{ textAlign: "center", padding: 12 }}>
                <div style={{ fontSize: 28, opacity: 0.3 }}>🖼️</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>
                  Nenhuma logo cadastrada
                </div>
              </div>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: C.textMuted,
                marginBottom: 4,
              }}
            >
              Logo da empresa
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 12, lineHeight: 1.4 }}>
              PNG ou JPG, até 2 MB. Aparece na sidebar do sistema. É redimensionada
              automaticamente pra caber em 400×400.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: C.accent,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: uploadingLogo ? "wait" : "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  opacity: uploadingLogo ? 0.6 : 1,
                }}
              >
                {uploadingLogo ? "Enviando..." : "Enviar nova logo"}
              </button>
              {profile?.logoUrl && (
                <button
                  type="button"
                  onClick={handleLogoRemove}
                  disabled={uploadingLogo}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                    background: "transparent",
                    color: C.textMuted,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: uploadingLogo ? "wait" : "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    opacity: uploadingLogo ? 0.6 : 1,
                  }}
                >
                  Remover
                </button>
              )}
            </div>
            {logoError && (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  background: C.redDim,
                  color: C.red,
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {logoError}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg"
              onChange={handleFileSelected}
              style={{ display: "none" }}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginBottom: 8,
          }}
        >
          <FormField label="Nome" required span={2}>
            <input
              value={form.nome}
              onChange={(e) => update("nome", e.target.value)}
              style={inputStyle}
              placeholder="Luciana Bragato"
            />
          </FormField>

          <FormField label="Empresa / Razão social" span={2}>
            <input
              value={form.empresa}
              onChange={(e) => update("empresa", e.target.value)}
              style={inputStyle}
              placeholder="Bragato Turismo"
            />
          </FormField>

          <FormField label="Documento / IVA" required>
            <input
              value={form.documento}
              onChange={(e) => update("documento", e.target.value)}
              style={inputStyle}
              placeholder="02824640029"
            />
          </FormField>

          <FormField label="Telefone">
            <input
              value={form.telefone}
              onChange={(e) => update("telefone", e.target.value)}
              style={inputStyle}
              placeholder="+39 348 264 8202"
            />
          </FormField>

          <FormField label="E-mail" span={2}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              style={inputStyle}
            />
          </FormField>
        </div>

        {/* ─── Dados bancários / PIX ─── */}
        <div style={sectionLabel(C)}>Dados para pagamento</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 14,
            marginBottom: 8,
          }}
        >
          <FormField label="Banco" span={3}>
            <input
              value={form.bancoNome}
              onChange={(e) => update("bancoNome", e.target.value)}
              style={inputStyle}
              placeholder="Itaú"
            />
          </FormField>

          <FormField label="Agência">
            <input
              value={form.bancoAgencia}
              onChange={(e) => update("bancoAgencia", e.target.value)}
              style={inputStyle}
            />
          </FormField>

          <FormField label="Conta" span={2}>
            <input
              value={form.bancoConta}
              onChange={(e) => update("bancoConta", e.target.value)}
              style={inputStyle}
            />
          </FormField>

        </div>

        {/* ─── Chaves PIX (múltiplas) ───
            Cada linha = 1 chave (tipo + valor + botão X). Botão "Adicionar"
            no rodapé até MAX_CHAVES_PIX. Todas aparecem no PDF do orçamento. */}
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted }}>
              Chaves PIX
            </div>
            <div style={{ fontSize: 11, color: C.textDim }}>
              {form.chavesPix.length}/{MAX_CHAVES_PIX}
            </div>
          </div>

          {form.chavesPix.length === 0 && (
            <div
              style={{
                padding: "10px 12px",
                border: `1px dashed ${C.border}`,
                borderRadius: 8,
                fontSize: 12,
                color: C.textDim,
                marginBottom: 8,
              }}
            >
              Nenhuma chave cadastrada. Adicione ao menos 1 pra aparecer no PDF.
            </div>
          )}

          {form.chavesPix.map((chave, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "160px 1fr auto",
                gap: 8,
                marginBottom: 8,
                alignItems: "center",
              }}
            >
              <select
                value={chave.tipo}
                onChange={(e) => updateChave(idx, "tipo", e.target.value)}
                style={inputStyle}
              >
                {Object.entries(TIPO_CHAVE_PIX).map(([k, v]) => (
                  <option key={v} value={v}>
                    {TIPO_CHAVE_PIX_LABEL[v]}
                  </option>
                ))}
              </select>
              <input
                value={chave.valor}
                onChange={(e) => updateChave(idx, "valor", e.target.value)}
                style={inputStyle}
                placeholder="121.138.518-30"
              />
              <button
                type="button"
                onClick={() => removeChave(idx)}
                title="Remover esta chave"
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: "transparent",
                  color: C.textMuted,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
          ))}

          {form.chavesPix.length < MAX_CHAVES_PIX && (
            <button
              type="button"
              onClick={addChave}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px dashed ${C.accent}`,
                background: "transparent",
                color: C.accent,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                marginTop: 4,
              }}
            >
              + Adicionar chave PIX
            </button>
          )}
        </div>

        {error && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 14px",
              background: C.redDim,
              color: C.red,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 22,
            paddingTop: 18,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: "10px 24px",
              borderRadius: 10,
              border: "none",
              background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})`,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? "wait" : "pointer",
              opacity: saving ? 0.7 : 1,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {saving ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </form>

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "12px 20px",
            background: C.bgCard,
            border: `1px solid ${C.green || C.accent}`,
            borderRadius: 10,
            color: C.text,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            zIndex: 2000,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function sectionLabel(C) {
  return {
    fontSize: 11,
    fontWeight: 700,
    color: C.accent,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginTop: 10,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: `1px solid ${C.border}`,
  };
}
